/**
 * OVIDIO · PDP — JS mínimo (vanilla, módulo ES).
 * - <ovd-bundles>: tarjetas de pack → fija la cantidad del formulario nativo de Horizon,
 *   recalcula precios al cambiar de variante (evento shopify:product:select) y gestiona la barra fija.
 * - <ovd-size-guide>: abre/cierra la guía de tallas (dialog nativo).
 * - <ovd-compare>: comparador sin / con.
 * - <ovd-carousel>: flechas del carrusel de testimonios.
 * - [data-ovd-go="packs"]: desplaza hasta los packs.
 * El precio que se cobra NO lo calcula este archivo: lo aplica Shopify con descuentos automáticos.
 */
import { formatMoney } from '@theme/money-formatting';

const PRODUCT_SELECT = 'shopify:product:select';

function money(cents, el) {
  const format = el?.dataset.moneyFormat || '{{amount_with_comma_separator}} €';
  const currency = el?.dataset.currency || 'EUR';
  try {
    return formatMoney(Math.round(cents), format, currency);
  } catch (e) {
    return (Math.round(cents) / 100).toFixed(2).replace('.', ',') + ' €';
  }
}

function toneFromTitle(title) {
  const t = (title || '').toLowerCase();
  if (/(piel|nude|flesh|beige|carne)/.test(t)) return 'skin';
  if (/(blanc|white)/.test(t)) return 'light';
  return 'dark';
}

/* ---------------------------------------------------------------- packs */
class OvdBundles extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section') || document;
    this.price = Number(this.dataset.price) || 0;
    this.compare = Number(this.dataset.comparePrice) || 0;
    this.available = this.dataset.available !== 'false';
    this.variantTitle = this.dataset.variantTitle || '';
    this.inputs = Array.from(this.querySelectorAll('input[type="radio"]'));

    if (this.inputs.length && !this.inputs.some((i) => i.checked)) this.inputs[0].checked = true;

    this.onChange = () => {
      this.applyQuantity();
      this.render();
    };
    this.inputs.forEach((i) => i.addEventListener('change', this.onChange));

    this.onProductSelect = (event) => this.handleProductSelect(event);
    this.section.addEventListener(PRODUCT_SELECT, this.onProductSelect);

    this.setupSticky();
    this.render();
    customElements.whenDefined('quantity-selector-component').then(() => this.applyQuantity());
    // Por si el formulario se hidrata más tarde
    setTimeout(() => this.applyQuantity(), 600);
  }

  disconnectedCallback() {
    this.inputs?.forEach((i) => i.removeEventListener('change', this.onChange));
    this.section?.removeEventListener(PRODUCT_SELECT, this.onProductSelect);
    this.stickyObserver?.disconnect();
    if (this.sticky && this.sticky.parentElement === document.body) this.sticky.remove();
  }

  get selected() {
    return this.inputs.find((i) => i.checked) || this.inputs[0];
  }

  get quantity() {
    return Number(this.selected?.dataset.qty) || 1;
  }

  get productForm() {
    return this.section.querySelector('product-form-component');
  }

  applyQuantity() {
    const qty = String(this.quantity);
    const qs = this.section.querySelector('product-form-component quantity-selector-component');
    if (qs && typeof qs.setValue === 'function') {
      qs.setValue(qty);
      if (typeof qs.updateButtonStates === 'function') {
        try { qs.updateButtonStates(); } catch (e) { /* no-op */ }
      }
      return;
    }
    // Sin selector de cantidad: aseguramos un input "quantity" dentro del formulario nativo
    const form = this.productForm?.querySelector('form[data-type="add-to-cart-form"], form');
    if (!form) return;
    let input = form.querySelector('input[name="quantity"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'quantity';
      form.appendChild(input);
    }
    input.value = qty;
  }

  calc(input) {
    const qty = Number(input.dataset.qty) || 1;
    const type = input.dataset.type;
    const value = Number(input.dataset.value) || 0;
    const total = this.price * qty;
    // Precio de referencia: compare-at de la variante (si es mayor que el precio) o el precio normal
    const compareTotal = Math.max(this.compare, this.price) * qty;
    let final = total;
    if (type === 'percent') final = Math.round((total * (100 - value)) / 100);
    if (type === 'free') final = this.price * Math.max(1, qty - value);
    return { qty, total: compareTotal, final, unit: Math.round(final / qty), saved: compareTotal - final };
  }

  render() {
    this.inputs.forEach((input) => {
      const card = input.closest('.ovd-pack');
      if (!card) return;
      const c = this.calc(input);
      const finalEl = card.querySelector('[data-ovd-final]');
      const compareEl = card.querySelector('[data-ovd-compare]');
      const savingsEl = card.querySelector('[data-ovd-savings]');
      if (finalEl) finalEl.textContent = money(c.final, this);
      if (compareEl) {
        compareEl.textContent = money(c.total, this);
        compareEl.hidden = !(c.final < c.total);
      }
      if (savingsEl && input.dataset.savings) {
        savingsEl.textContent = input.dataset.savings
          .replace('[ud]', money(c.unit, this))
          .replace('[ahorro]', money(c.saved, this));
      }
    });
    this.dataset.tone = toneFromTitle(this.variantTitle);
    this.renderSticky();
  }

  handleProductSelect(event) {
    if (!event?.promise) return;
    event.promise
      .then(({ detail } = {}) => {
        const variant = detail?.resource;
        if (!variant) return;
        this.price = Number(variant.price) || this.price;
        this.compare = Number(variant.compare_at_price) || 0;
        this.available = variant.available !== false;
        this.variantTitle = variant.title || '';
        const src = variant.featured_image?.src || variant.featured_media?.preview_image?.src;
        if (src && this.stickyImg) this.stickyImg.src = src + (src.includes('?') ? '&' : '?') + 'width=120';
        this.render();
        // Horizon conserva la cantidad al cambiar de variante; la reaplicamos por seguridad
        setTimeout(() => this.applyQuantity(), 60);
      })
      .catch(() => {});
  }

  /* barra fija */
  setupSticky() {
    const sticky = this.querySelector('[data-ovd-sticky]');
    if (!sticky) return;
    this.sticky = sticky;
    document.body.appendChild(sticky); // fuera de contenedores con transform/overflow
    this.stickyImg = sticky.querySelector('img');
    this.stickyBtn = sticky.querySelector('[data-ovd-sticky-btn]');
    this.stickyBtn?.addEventListener('click', () => {
      const btn = this.section.querySelector(
        'product-form-component [ref="addToCartButton"], product-form-component button[name="add"]'
      );
      if (btn && !btn.disabled) {
        btn.click();
      } else {
        this.scrollToPacks();
      }
    });

    const target = this.section.querySelector('.buy-buttons-block') || this;
    this.stickyObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.classList.toggle('is-on', passed);
        sticky.setAttribute('aria-hidden', String(!passed));
        if (this.stickyBtn) this.stickyBtn.tabIndex = passed ? 0 : -1;
      },
      { threshold: 0 }
    );
    this.stickyObserver.observe(target);
  }

  renderSticky() {
    if (!this.sticky) return;
    const c = this.calc(this.selected);
    const units = c.qty > 1 ? this.dataset.unitsLabel || 'uds' : this.dataset.unitLabel || 'ud';
    const v = this.sticky.querySelector('[data-ovd-sticky-v]');
    const p = this.sticky.querySelector('[data-ovd-sticky-price]');
    const s = this.sticky.querySelector('[data-ovd-sticky-compare]');
    if (v) v.textContent = [this.variantTitle, `${c.qty} ${units}`].filter(Boolean).join(' · ');
    if (p) p.textContent = money(c.final, this);
    if (s) s.textContent = c.final < c.total ? money(c.total, this) : '';
    if (this.stickyBtn) this.stickyBtn.disabled = !this.available;
  }

  scrollToPacks() {
    this.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}

/* ---------------------------------------------------------- guía de tallas */
class OvdSizeGuide extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.opener = this.querySelector('[data-ovd-open]');
    this.opener?.addEventListener('click', () => this.open());
    this.querySelector('[data-ovd-close]')?.addEventListener('click', () => this.close());
    this.dialog?.addEventListener('click', (e) => {
      if (e.target === this.dialog) this.close();
    });
  }

  open() {
    if (!this.dialog) return;
    if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
    else this.dialog.setAttribute('open', '');
  }

  close() {
    if (!this.dialog) return;
    if (typeof this.dialog.close === 'function') this.dialog.close();
    else this.dialog.removeAttribute('open');
    this.opener?.focus();
  }
}

/* --------------------------------------------------------- comparador */
class OvdCompare extends HTMLElement {
  connectedCallback() {
    const range = this.querySelector('input[type="range"]');
    range?.addEventListener('input', () => this.style.setProperty('--p', `${range.value}%`));
  }
}

/* ----------------------------------------------------------- carrusel */
class OvdCarousel extends HTMLElement {
  connectedCallback() {
    const track = this.querySelector('[data-ovd-track]');
    const step = (dir) => {
      const card = track?.firstElementChild;
      if (!track || !card) return;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 12;
      track.scrollBy({ left: dir * (card.getBoundingClientRect().width + gap), behavior: 'smooth' });
    };
    this.querySelector('[data-ovd-prev]')?.addEventListener('click', () => step(-1));
    this.querySelector('[data-ovd-next]')?.addEventListener('click', () => step(1));
  }
}

const define = (name, ctor) => {
  if (!customElements.get(name)) customElements.define(name, ctor);
};
define('ovd-bundles', OvdBundles);
define('ovd-size-guide', OvdSizeGuide);
define('ovd-compare', OvdCompare);
define('ovd-carousel', OvdCarousel);

/* ------------------------------------------------ delegación global */
if (!window.__ovdDelegated) {
  window.__ovdDelegated = true;
  document.addEventListener('click', (event) => {
    const go = event.target.closest?.('[data-ovd-go="packs"]');
    if (go) {
      const target =
        document.querySelector('ovd-bundles') ||
        document.querySelector('.buy-buttons-block') ||
        document.querySelector('product-form-component');
      if (target) {
        event.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    const openSize = event.target.closest?.('[data-ovd-open-size]');
    if (openSize) {
      const scope = openSize.closest('.shopify-section') || document;
      const guide = scope.querySelector('ovd-size-guide') || document.querySelector('ovd-size-guide');
      if (guide && typeof guide.open === 'function') {
        event.preventDefault();
        guide.open();
      }
    }
  });
}
